/**
 * FB-1457 — pause/play for the theme's two perpetual motions.
 *
 * Findings 81087 and 81311 (both WCAG 2.2.2 Pause, Stop, Hide,
 * `content-motion-uninterruptible`). Two components move forever with no way
 * to stop them:
 *
 *   81087  the announcement-bar carousel (`#x-slide-announcement-bar`), which
 *          auto-advances on a Splide autoplay timer.
 *   81311  the scrolling banner, a CSS marquee whose only pause was `:hover`
 *          — pointer-only, so it does not exist for a keyboard or
 *          switch-device user, and it is not a persistent choice for anyone.
 *
 * Both now render `snippets/custom-motion-toggle.liquid` and this file drives
 * it.
 *
 * Why this is a plain `defer` script and not an Alpine component: the theme
 * defers most of its JS until the first touch/scroll/mouseover (see
 * `layout/theme.liquid`, the `init-run` dispatch). A control bound through
 * Alpine would therefore do nothing for a keyboard-only user arriving on a
 * cold load — the exact population 2.2.2 protects. Everything here binds on
 * parse.
 *
 * Why the announcement bar does not use Splide's own `.splide__toggle`:
 * Splide 4.1.3 does support one, and it manages `aria-label`, `aria-controls`
 * and an `is-active` class for free — but it only finds the button *inside*
 * the carousel root, and that root is `overflow-hidden` with both of its
 * padding gutters occupied by the prev/next arrows at >=1024px. There is no
 * free edge to put it on. The button therefore sits outside the root as a flex
 * sibling, and the semantics Splide would have managed are reproduced here:
 * the label swap below, and `aria-controls` pointed at the track once Splide
 * has named it.
 *
 * Splide's `Autoplay.pause()` is the right call rather than a hand-rolled
 * timer stop. Called with its default argument it sets the component's
 * `stopped` flag, which suppresses the `pauseOnHover` / `pauseOnFocus`
 * auto-toggle — so a deliberate pause survives the pointer leaving the bar.
 * `play()` clears it and hands hover/focus behavior back.
 */
(() => {
  'use strict';

  if (window.__fbA11yMotionControls) return;
  window.__fbA11yMotionControls = true;

  const TOGGLE_SELECTOR = '[data-fb-motion-toggle]';

  /**
   * The marquee's `animation` shorthand reads `var(--play-state, running)` —
   * see `snippets/css-variables.liquid`, `.animate-scroll-banner`. Writing the
   * property inline on the region is what pauses it, and inline beats the
   * reduced-motion rule in `assets/custom-theme.css.liquid`, so a user who
   * presses Play under `prefers-reduced-motion` gets motion.
   */
  const PLAY_STATE_PROPERTY = '--play-state';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isPaused = (button) => button.getAttribute('data-fb-motion-state') === 'paused';

  /** Writes the state the CSS keys its icon swap off, and the matching name. */
  const setState = (button, paused) => {
    button.setAttribute('data-fb-motion-state', paused ? 'paused' : 'playing');

    const label = button.getAttribute(paused ? 'data-fb-motion-label-play' : 'data-fb-motion-label-pause');
    if (label) button.setAttribute('aria-label', label);
  };

  const targetOf = (button) => {
    const selector = button.getAttribute('data-fb-motion-target');
    return selector ? document.querySelector(selector) : null;
  };

  const autoplayOf = (root) => {
    // `assets/theme.js` (xSplide store) assigns `root.splide` before mounting.
    const splide = root && root.splide;
    return (splide && splide.Components && splide.Components.Autoplay) || null;
  };

  const applyMarquee = (region, paused) => {
    region.style.setProperty(PLAY_STATE_PROPERTY, paused ? 'paused' : 'running');
  };

  const applySplide = (root, paused) => {
    const autoplay = autoplayOf(root);
    if (!autoplay) return false;

    if (paused) {
      autoplay.pause();
    } else {
      autoplay.play();
    }
    return true;
  };

  const apply = (button, paused) => {
    const target = targetOf(button);
    if (!target) return;

    if (button.getAttribute('data-fb-motion-type') === 'splide') {
      // A press before Splide mounts cannot reach the autoplay timer. The
      // intent is still recorded on the button, and `whenSplideReady` replays
      // it, so the carousel never starts moving against an explicit pause.
      applySplide(target, paused);
    } else {
      applyMarquee(target, paused);
    }
  };

  /**
   * Runs `callback` every time Splide finishes mounting on `root`, which it
   * marks by adding `is-initialized` at the end of `mount()`.
   *
   * "Every time", not once. Two reasons. The carousel mounts from
   * `x-intersect.once`, which needs Alpine, so on a cold load the first mount
   * can be several seconds — or a scroll — away. And when
   * `enable_carousel_on_desktop` is off, the announcement bar's config destroys
   * and re-creates Splide as the viewport crosses 768px, so a pause chosen on
   * mobile has to be re-applied to each new instance rather than lost at the
   * first resize.
   */
  const onSplideInit = (root, callback) => {
    let initialized = root.classList.contains('is-initialized');
    if (initialized) callback();

    new MutationObserver(() => {
      const nowInitialized = root.classList.contains('is-initialized');
      if (nowInitialized === initialized) return;

      initialized = nowInitialized;
      if (initialized) callback();
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
  };

  const initSplideToggle = (button, root) => {
    onSplideInit(root, () => {
      // Splide names the track `<rootId>-track` on init — the very element the
      // audit reported. Point the control at it now that the id exists.
      const track = root.querySelector('.splide__track');
      if (track && track.id && !button.hasAttribute('aria-controls')) {
        button.setAttribute('aria-controls', track.id);
      }

      // Honour a reduced-motion preference, and replay a press that landed
      // before the carousel existed.
      if (isPaused(button)) applySplide(root, true);
    });
  };

  const initToggle = (button) => {
    const target = targetOf(button);
    if (!target) {
      // Nothing to control. Leaving a live-looking button on the page is its
      // own defect, so remove it rather than ship a no-op.
      button.remove();
      return;
    }

    // Reduced motion starts both components paused, with the button showing
    // Play so the choice stays the user's. Nothing is written to the marquee
    // here: the media query in `custom-theme.css.liquid` already holds it
    // still, and staying out of the inline style is exactly what lets a later
    // Play press override that rule.
    if (prefersReducedMotion()) setState(button, true);

    if (button.getAttribute('data-fb-motion-type') === 'splide') {
      initSplideToggle(button, target);
    }

    button.addEventListener('click', () => {
      const paused = !isPaused(button);
      setState(button, paused);
      apply(button, paused);
    });
  };

  document.querySelectorAll(TOGGLE_SELECTOR).forEach(initToggle);
})();
